import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { landingStyles } from './landing-page.styles.js';
import { featuresPageStyles } from './features-page.styles.js';

type FeatureModule = {
  id: string;
  title: string;
  capabilities: string[];
  benefits: string[];
  users: string;
  image: string;
  imageAlt: string;
};

const FEATURE_MODULES: FeatureModule[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    capabilities: [
      'Monitor KPIs, downtime, open work orders, overdue PMs, and incidents.',
      'Track asset health with real-time charts and performance widgets.',
      'Drill into alerts and trends to identify emerging issues fast.',
    ],
    benefits: [
      'Gives managers instant visibility into maintenance operations.',
      'Supports fast decision-making to keep production on track.',
      'Reduces unplanned downtime with proactive alerting.',
    ],
    users: 'Admins, Managers',
    image: '/assets/screenshots/dashboard.svg',
    imageAlt: 'Dashboard overview',
  },
  {
    id: 'assets',
    title: 'Asset Management',
    capabilities: [
      'Create and manage detailed asset records with hierarchy and locations.',
      'Store photos, costs, warranties, and custom data fields.',
      'Link assets directly to work orders, PM tasks, and documents.',
    ],
    benefits: [
      'Maintain complete asset history for better lifecycle tracking.',
      'Simplify compliance and audit preparation with accurate records.',
      'Surface high-cost or high-risk assets for strategic planning.',
    ],
    users: 'Admins, Engineers',
    image: '/assets/screenshots/assets.svg',
    imageAlt: 'Asset register',
  },
  {
    id: 'work-orders',
    title: 'Work Orders',
    capabilities: [
      'Create corrective and preventive work orders with rich detail.',
      'Assign jobs, set priorities (Low → Critical), and attach parts/documents.',
      'Track status from open to in-progress to completed with timestamps.',
    ],
    benefits: [
      'Streamlines maintenance workflows and eliminates manual handoffs.',
      'Reduces miscommunication between teams and shifts.',
      'Provides audit-ready compliance records of every job.',
    ],
    users: 'Technicians, Engineers, Managers',
    image: '/assets/screenshots/work-orders.svg',
    imageAlt: 'Work order board',
  },
  {
    id: 'pm',
    title: 'Preventive Maintenance',
    capabilities: [
      'Schedule recurring PM tasks with calendar or meter-based triggers.',
      'Auto-generate work orders and update next due dates automatically.',
      'Adjust schedules when tasks complete or assets are offline.',
    ],
    benefits: [
      'Cuts unplanned breakdowns and emergency repairs.',
      'Improves asset reliability and uptime across the facility.',
      'Extends equipment life with disciplined maintenance routines.',
    ],
    users: 'Admins, Engineers',
    image: '/assets/screenshots/pm.svg',
    imageAlt: 'Preventive maintenance scheduler',
  },
  {
    id: 'parts',
    title: 'Parts & Inventory',
    capabilities: [
      'Manage spare parts catalogs with stock levels and reorder points.',
      'Track usage history, supplier references, and pricing.',
      'Reserve parts directly from work orders to maintain availability.',
    ],
    benefits: [
      'Prevents costly stockouts during critical repairs.',
      'Reduces excess inventory and carrying costs.',
      'Ensures technicians have the right parts when the job starts.',
    ],
    users: 'Storekeepers, Engineers, Managers',
    image: '/assets/screenshots/parts.svg',
    imageAlt: 'Parts inventory management',
  },
  {
    id: 'locations',
    title: 'Locations',
    capabilities: [
      'Organize assets, teams, and parts by plant, building, or area.',
      'Review maintenance history and KPIs at the site level.',
      'Visualize asset distribution and workload across locations.',
    ],
    benefits: [
      'Makes multi-site operations easier to coordinate.',
      'Delivers faster reporting for regional or site managers.',
      'Improves accountability with location-specific metrics.',
    ],
    users: 'Admins, Managers',
    image: '/assets/screenshots/locations.svg',
    imageAlt: 'Location performance view',
  },
  {
    id: 'health-safety',
    title: 'Health & Safety',
    capabilities: [
      'Record incidents, near misses, and corrective actions.',
      'Plan audits and safety inspections with follow-up tasks.',
      'Link safety issues to work orders and generate compliance reports.',
    ],
    benefits: [
      'Improves workplace safety culture and visibility.',
      'Supports regulatory compliance with complete documentation.',
      'Reduces risk by closing the loop on hazards quickly.',
    ],
    users: 'Admins, Safety Managers',
    image: '/assets/screenshots/hs.svg',
    imageAlt: 'Safety command center',
  },
];

@customElement('pristinemaint-features')
export class PristineMaintFeatures extends LitElement {
  static styles = [landingStyles, featuresPageStyles];

  protected firstUpdated() {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  private emitNavigate(route: string, section?: string) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { route, section },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private navHandler(route: string, section?: string) {
    return (event: Event) => {
      event.preventDefault();
      this.emitNavigate(route, section);
    };
  }

  private handleLogoKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.emitNavigate('/');
    }
  }

  render() {
    return html`
      <div class="landing-container">
        <header class="header">
          <div
            class="logo"
            @click=${this.navHandler('/')}
            @keydown=${this.handleLogoKeydown}
            role="link"
            tabindex="0"
          >
            <img
              src="https://firebasestorage.googleapis.com/v0/b/work-order-managment.firebasestorage.app/o/pristinemaint.png?alt=media&token=2dd117e4-d4e7-4bd6-b666-548c5068b566"
              alt="PristineMaint Logo"
              class="logo-image"
            />
            <span class="logo-text">PristineMaint</span>
          </div>

          <nav class="nav-links">
            <a href="/features" class="nav-link active" aria-current="page" @click=${this.navHandler('/features')}>Features</a>
            <a href="/pricing" class="nav-link" @click=${this.navHandler('/pricing')}>Pricing</a>
            <a href="#about" class="nav-link">About</a>
            <a href="#contact" class="nav-link" @click=${this.navHandler('/', 'contact')}>Contact</a>
          </nav>

          <div class="header-actions">
            <button class="btn btn-secondary" @click=${this.navHandler('/', 'features')}>Learn More</button>
            <button class="btn btn-primary" @click=${this.navHandler('/', 'contact')}>Get Started</button>
          </div>
        </header>

        <main class="feature-page">
          <section class="feature-page-hero">
            <h1>Explore the PristineMaint Platform</h1>
            <p>
              Dive deeper into each module to see how PristineMaint unifies maintenance planning,
              execution, and reporting across your operations.
            </p>
            <div class="feature-page-hero-actions">
              <button class="btn btn-primary" @click=${this.navHandler('/', 'contact')}>Talk to Sales</button>
              <button class="btn btn-secondary" @click=${this.navHandler('/')}>Back to Overview</button>
            </div>
          </section>

          <section class="feature-page-grid">
            ${FEATURE_MODULES.map(
              (module) => html`
                <article class="feature-module" id=${module.id}>
                  <div class="feature-module-content">
                    <h2>${module.title}</h2>
                    <div class="feature-module-group">
                      <h3>Capabilities</h3>
                      <ul>
                        ${module.capabilities.map((item) => html`<li>${item}</li>`)}
                      </ul>
                    </div>
                    <div class="feature-module-group">
                      <h3>Benefits</h3>
                      <ul>
                        ${module.benefits.map((item) => html`<li>${item}</li>`)}
                      </ul>
                    </div>
                    <p class="feature-panel-meta"><strong>Users:</strong> ${module.users}</p>
                  </div>

                  <div class="feature-module-preview">
                    <img src=${module.image} alt=${module.imageAlt} class="feature-screenshot" />
                  </div>
                </article>
              `,
            )}
          </section>
        </main>

        <section class="feature-page-cta">
          <h2>Ready to see PristineMaint in action?</h2>
          <p>
            We tailor onboarding and configuration to your workflows. Share a few details and our team will
            schedule a guided walkthrough of the features that matter most.
          </p>
          <div class="feature-page-cta-actions">
            <button class="btn btn-white" @click=${this.navHandler('/', 'contact')}>Contact Us</button>
            <button class="btn btn-secondary" @click=${this.navHandler('/')}>Return to Home</button>
          </div>
        </section>

        <footer class="footer">
          <div class="footer-container">
            <div class="footer-section">
              <h3>PristineMaint</h3>
              <p>
                Advanced CMMS solutions for modern businesses. Streamline your maintenance operations with our
                cutting-edge platform.
              </p>
            </div>

            <div class="footer-section">
              <h3>Product</h3>
              <ul class="footer-links">
                <li><a href="/features" @click=${(event: Event) => this.handleCurrentNav(event)}>Features</a></li>
                <li><a href="/pricing" @click=${this.navHandler('/pricing')}>Pricing</a></li>
                <li><a href="#demo">Demo</a></li>
                <li><a href="#integrations">Integrations</a></li>
              </ul>
            </div>

            <div class="footer-section">
              <h3>Company</h3>
              <ul class="footer-links">
                <li><a href="#about">About Us</a></li>
                <li><a href="#careers">Careers</a></li>
                <li><a href="#blog">Blog</a></li>
                <li><a href="#press">Press</a></li>
              </ul>
            </div>

            <div class="footer-section">
              <h3>Support</h3>
              <ul class="footer-links">
                <li><a href="#help">Help Center</a></li>
                <li>
                  <a href="mailto:info@pristinemaint.com?subject=Support Request&body=Hello, I have a question about PristineMaint.">
                    Contact Us
                  </a>
                </li>
                <li><a href="#status">Status</a></li>
                <li><a href="#security">Security</a></li>
              </ul>
            </div>
          </div>

          <div class="footer-bottom">
            <p>&copy; 2024 PristineMaint. All rights reserved.</p>
          </div>
        </footer>
      </div>
    `;
  }
}
